import { Component, computed, DestroyRef, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { ProjectsService } from '../../services/projects.service';
import { TableModule } from '@openng/optimus-ui/table';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { of } from 'rxjs';
import { ConfirmHelperService } from '@zayan-erp/shared';
import { Lot } from '../../models/lots.models';
import { SitingAnalysisLot, SitingAnalysisLotModel } from '../../models/project.models';

@Component({
  selector: 'lib-project-siting-analysis',
  imports: [TableModule, ConfirmDialogModule],
  providers: [],
  templateUrl: './project-siting-analysis.component.html',
  styleUrl: './project-siting-analysis.component.scss'
})
export class ProjectSitingAnalysisComponent {
  projectId = signal<string | null>(inject(ActivatedRoute).snapshot.params['id'] ?? null);
  projectName = input<string>();
  #projectsService = inject(ProjectsService);
  #destroyRef = inject(DestroyRef);
  #confirmationService = inject(ConfirmHelperService);

  fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  sitingAnalysisResource = rxResourceFixed({
    params: () => ({ projectId: this.projectId() }),
    defaultValue: { projectId: '', lots: [], lotNumbers: [], modelNames: [], cells: [] },
    stream: ({ params }) => {
      if (!params.projectId || params.projectId === 'new') {
        return of({ projectId: '', lots: [], lotNumbers: [], modelNames: [], cells: [] });
      }
      return this.#projectsService.getProjectSitingAnalysis(params.projectId);
    }
  });

  modelsResource = rxResourceFixed({
    params: () => ({ projectId: this.projectId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.projectId || params.projectId === 'new') {
        return of([]);
      }
      return this.#projectsService.readProjectModels(params.projectId);
    }
  });

  lotsResource = rxResourceFixed({
    params: () => ({ projectId: this.projectId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.projectId || params.projectId === 'new') {
        return of([]);
      }
      return this.#projectsService.getProjectLots(params.projectId, false);
    }
  });

  models = computed(() => this.modelsResource.value() ?? []);
  lots = computed(() => this.lotsResource.value() ?? []);

  sitingAnalysis = computed(
    () =>
      this.sitingAnalysisResource.value() ?? {
        projectId: '',
        lots: [],
        lotNumbers: [],
        modelNames: [],
        cells: []
      }
  );

  normalizedSitingLots = computed(() => this.sitingAnalysis().lots ?? []);

  #normalize(value: string | null | undefined): string {
    return (value ?? '').trim().toLowerCase();
  }

  #modelKey(modelId: string | null | undefined, modelName: string | null | undefined): string {
    return `${this.#normalize(modelId)}::${this.#normalize(modelName)}`;
  }

  #modelElevationKey(
    modelId: string | null | undefined,
    modelName: string | null | undefined,
    elevationId: string | null | undefined,
    elevationCode: string | null | undefined
  ): string {
    return `${this.#modelKey(modelId, modelName)}::${this.#normalize(elevationId)}::${this.#normalize(elevationCode)}`;
  }

  #getLotSitingData(lot: Lot): SitingAnalysisLot | undefined {
    const lotId = this.#normalize(lot.id);
    const lotNo = this.#normalize(lot.lotNo);
    return this.normalizedSitingLots().find(
      sitingLot => this.#normalize(sitingLot.lotId) === lotId || this.#normalize(sitingLot.lotNo) === lotNo
    );
  }

  #isCompatibleForColumn(lot: Lot, column: SitingColumn): boolean {
    const lotSiting = this.#getLotSitingData(lot);
    const lotModels = lotSiting?.models ?? [];

    const matchedModel = lotModels.find(
      model => this.#modelKey(model.modelId, model.modelName) === this.#modelKey(column.modelId, column.modelName)
    );
    if (!matchedModel) {
      return false;
    }
    return (matchedModel.elevations ?? []).some(
      elevation =>
        this.#modelElevationKey(
          matchedModel.modelId,
          matchedModel.modelName,
          elevation.elevationId,
          elevation.elevationCode
        ) === this.#modelElevationKey(column.modelId, column.modelName, column.elevationId, column.elevationCode)
    );
  }

  columns = computed<SitingColumn[]>(() => {
    const columnsMap = new Map<string, SitingColumn>();
    const modelsList = this.models();
    const sitingLots = this.normalizedSitingLots();

    modelsList.forEach((model: any) => {
      (model?.elevations ?? []).forEach((elevation: any) => {
        const key = this.#modelElevationKey(model.id, model.modelName, elevation.id, elevation.elevationCode);
        columnsMap.set(key, {
          key,
          label: `${model.modelName} - ${elevation.elevationCode}`,
          modelId: model.id,
          modelName: model.modelName,
          elevationId: elevation.id,
          elevationCode: elevation.elevationCode
        });
      });
    });

    sitingLots.forEach(lot => {
      (lot.models ?? []).forEach((model: SitingAnalysisLotModel) => {
        (model.elevations ?? []).forEach(elevation => {
          const key = this.#modelElevationKey(
            model.modelId,
            model.modelName,
            elevation.elevationId,
            elevation.elevationCode
          );
          columnsMap.set(key, {
            key,
            label: `${model.modelName} - ${elevation.elevationCode}`,
            modelId: model.modelId,
            modelName: model.modelName,
            elevationId: elevation.elevationId,
            elevationCode: elevation.elevationCode
          });
        });
      });
    });

    return Array.from(columnsMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  });

  tableData = computed(() => {
    const lotsList = this.lots();
    const columns = this.columns();

    return lotsList.map(lot => {
      const row: Record<string, unknown> = { lot, lotNo: lot.lotNo, lotId: lot.id };
      columns.forEach(column => {
        row[column.key] = this.#isCompatibleForColumn(lot, column) ? 'O' : 'X';
      });
      return row;
    });
  });

  /** Show grid when we have lots and columns to display */
  hasGridData = computed(() => this.lots().length > 0 && this.columns().length > 0);

  onImportClick(): void {
    this.fileInput()?.nativeElement.click();
  }

  onExportClick(): void {
    const pId = this.projectId();
    if (!pId || pId === 'new') return;

    const pName = this.projectName();
    const projectName = pName ? pName.toUpperCase() : 'PROJECT';
    const date = +new Date();
    const fileName = `${projectName}-${date}.xlsx`.replace(/\s+/g, '-');

    this.#projectsService
      .exportProjectSitingAnalysis(pId, fileName)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        error: err => {
          const message =
            err?.message ??
            err?.error?.message ??
            (Array.isArray(err?.error?.data) && err?.error?.data?.length > 0 ? String(err.error.data[0]) : null) ??
            'An error occurred while exporting.';
          this.showErrorPopup(message);
        }
      });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const projectId = this.projectId();
    if (!projectId || projectId === 'new') {
      return;
    }

    this.#projectsService
      .importProjectSitingAnalysis(projectId, file)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.sitingAnalysisResource.reload();
          // Reset file input
          if (input) {
            input.value = '';
          }
        },
        error: err => {
          input.value = '';
          const errors: unknown[] = err?.error?.errors;
          const message =
            (Array.isArray(errors) && errors.length > 0 ? errors.join('\n') : null) ??
            err?.error?.message ??
            'An error occurred while importing.';
          this.showErrorPopup(message);
        }
      });
  }

  showErrorPopup(message: string): void {
    this.#confirmationService.showModal({
      icon: 'confirm-icon',
      message,
      header: 'Error',
      acceptLabel: 'OK',
      acceptSeverity: 'primary',
      rejectLabel: ''
    });
  }
}

interface SitingColumn {
  key: string;
  label: string;
  modelId: string;
  modelName: string;
  elevationId?: string;
  elevationCode?: string;
}
